import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Bell, MessageSquare, Heart, ThumbsUp, Star, CheckCheck, Trash2, Sparkles, ExternalLink, ChevronDown, ChevronUp, Youtube } from 'lucide-react';
import { getAuthHeaders } from '../../utils/auth';
import VideoEmbedModal from '../VideoEmbedModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

// Older notifications (pre-dating the videos[] field) only carry a single
// YouTube URL — pull the id out of it so they still open in-site.
function extractYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

// Shown when a notification refers to more than one upload — lets the user
// pick which one to watch instead of always landing on the newest.
const VideoPickerModal = ({ notif, onSelect, onClose }) => {
  const videos = notif.relatedContent?.videos || [];

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#111827',
          border: '1px solid rgba(139,92,246,0.25)',
          borderRadius: '16px',
          padding: '24px',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
            background: 'rgba(139,92,246,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Youtube size={18} color="#EF4444" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: '#FFFFFF', fontWeight: '700', fontSize: '15px',
              overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {notif.message}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: '12px', marginTop: '2px' }}>
              {videos.length} new uploads
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0 }}
          >×</button>
        </div>

        {/* Video grid */}
        <div style={{ color: '#9CA3AF', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
          New Uploads
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {videos.map((v, i) => (
            <div
              key={v.videoId || i}
              onClick={() => onSelect(v)}
              style={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', background: '#1F2937', display: 'flex', flexDirection: 'column' }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <img
                src={v.thumbnail || ''}
                alt={v.title}
                style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', flexShrink: 0, background: '#1F2937' }}
                onError={(e) => { e.target.onerror = null; e.target.style.background = '#374151'; }}
              />
              <div style={{ padding: '7px 8px 8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{
                  fontSize: '11px', color: '#D1D5DB', lineHeight: '1.35',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {v.title}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

const NotificationsPanel = ({ notifications = [], compact = false, priority = false, limit = null }) => {
  const [hoveredNotif, setHoveredNotif] = useState(null);
  const [expandedNotif, setExpandedNotif] = useState(null);
  const [localNotifications, setLocalNotifications] = useState(notifications);

  // `notifications` is only used as the initial value above — without this,
  // a parent refetch (Refresh button, or data that changed server-side after
  // this panel first mounted) never reaches this component's own state.
  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  const [embedVideo, setEmbedVideo] = useState(null);
  const [pickerNotif, setPickerNotif] = useState(null);
  const unreadCount = localNotifications.filter(n => !n.read).length;

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'reply': return { icon: <MessageSquare size={14} />, color: '#3B82F6' };
      case 'like': return { icon: <Heart size={14} />, color: '#EF4444' };
      case 'mention': return { icon: <Star size={14} />, color: '#8B5CF6' };
      case 'upvote': return { icon: <ThumbsUp size={14} />, color: '#22C55E' };
      case 'peak_moment_submitted': return { icon: <Sparkles size={14} />, color: '#F59E0B' };
      case 'peak_moment_approved': return { icon: <Star size={14} />, color: '#22C55E' };
      case 'new_reaction_media': return { icon: <Bell size={14} />, color: '#A78BFA' };
      case 'favorited_channel_new_video': return { icon: <Youtube size={14} />, color: '#EF4444' };
      default: return { icon: <Bell size={14} />, color: '#6B7280' };
    }
  };

  const handleMarkAsRead = async (notifId) => {
    try {
      await fetch(`${API_URL}/myspace/notifications/${notifId}/read`, {
        method: 'PUT', headers: getAuthHeaders()
      });
      setLocalNotifications(prev => prev.map(n => (n._id === notifId || n.id === notifId) ? { ...n, read: true } : n));
    } catch (error) { console.error('Error:', error); }
  };

  const handleDelete = async (notifId) => {
    try {
      await fetch(`${API_URL}/myspace/notifications/${notifId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      setLocalNotifications(prev => prev.filter(n => n._id !== notifId && n.id !== notifId));
    } catch (error) { console.error('Error:', error); }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/myspace/notifications/read-all`, {
        method: 'PUT', headers: getAuthHeaders()
      });
      setLocalNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) { console.error('Error:', error); }
  };

  const handleNotifClick = async (notif) => {
    const notifId = notif._id || notif.id;
    if (!notif.read) await handleMarkAsRead(notifId);

    // Any notification pointing at a reaction video opens in-site (same embed
    // as Home/Explore) instead of jumping to YouTube — covers both channel
    // uploads (favorited_channel_new_video) and liked-media matches
    // (new_reaction_media). Multiple uploads bundled into one notification
    // get a picker rather than always landing on whichever video was newest.
    if (notif.relatedContent?.type === 'video') {
      const videos = Array.isArray(notif.relatedContent?.videos) ? notif.relatedContent.videos : [];
      if (videos.length > 1) {
        setPickerNotif(notif);
        return;
      }
      const single = videos[0];
      const videoId = single?.videoId || extractYouTubeId(notif.relatedContent?.url);
      if (videoId) {
        setEmbedVideo({ videoId, title: single?.title || notif.relatedContent?.title || '' });
        return;
      }
    }

    const url = notif.relatedContent?.url;
    if (url) window.open(url, '_blank');
  };

  const toggleExpand = (e, index) => {
    e.stopPropagation();
    setExpandedNotif(prev => (prev === index ? null : index));
  };

  return (
    <div style={{ fontFamily: MANROPE }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: priority ? '12px' : '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Bell size={priority ? 17 : 16} color="#fbbf24" />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#FFFFFF', fontSize: priority ? '15px' : '14px', fontWeight: '700', fontFamily: SORA }}>Notifications</div>
            {!priority && (
              <div style={{ color: '#6f6b7d', fontSize: '11px', marginTop: '2px' }}>
                Mentions, replies, likes, and updates
              </div>
            )}
          </div>
          {unreadCount > 0 && (
            <span style={{
              background: '#dc2626', color: '#FFFFFF', fontSize: '10px', fontWeight: '700',
              padding: '2px 7px', borderRadius: '999px',
            }}>{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            style={{
              background: 'transparent', border: 'none', color: '#c4b5fd',
              fontSize: '11px', fontWeight: '600', fontFamily: MANROPE,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <CheckCheck size={12} /> Mark all
          </button>
        )}
      </div>

      {/* List */}
      {localNotifications.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: priority ? '8px' : '6px' }}>
          {localNotifications.slice(0, limit ?? (priority ? 4 : 10)).map((notif, index) => {
            const style = getNotificationStyle(notif.type);
            const isHovered = hoveredNotif === index;
            const isExpanded = expandedNotif === index;
            const notifId = notif._id || notif.id;
            const hasUrl = !!notif.relatedContent?.url;
            const actor = notif.actorUsername || notif.actor || '';
            const message = notif.message || '';
            // Avoid "ActorName ActorName reacted..." — strip leading actor name from message if duplicated
            const displayMessage = actor && message.toLowerCase().startsWith(actor.toLowerCase())
              ? message.slice(actor.length).trimStart()
              : message;
            const isLong = displayMessage.length > 80;

            return (
              <div
                key={notifId || index}
                style={{
                  padding: priority ? '12px' : '10px 12px',
                  background: isHovered
                    ? `${style.color}15`
                    : notif.read ? 'transparent' : priority ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.03)',
                  borderRadius: '10px',
                  cursor: hasUrl ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  borderLeft: notif.read ? '3px solid transparent' : `3px solid ${style.color}`,
                  boxShadow: priority && !notif.read ? `0 0 0 1px ${style.color}18` : 'none',
                  border: priority ? '1px solid rgba(255,255,255,0.04)' : 'none'
                }}
                onMouseEnter={() => setHoveredNotif(index)}
                onMouseLeave={() => setHoveredNotif(null)}
                onClick={() => handleNotifClick(notif)}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <div style={{ color: style.color, flexShrink: 0, marginTop: '2px' }}>{style.icon}</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      color: notif.read ? '#8f8b9c' : '#FFFFFF',
                      fontSize: priority ? '13px' : '12px', margin: 0, lineHeight: '1.5',
                      ...(isExpanded ? {} : {
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }),
                    }}>
                      {actor && <strong style={{ color: style.color }}>{actor} </strong>}
                      {displayMessage}
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <span style={{ color: '#6f6b7d', fontSize: '10px' }}>
                        {formatDate(notif.createdAt || notif.timestamp)}
                      </span>
                      {hasUrl && (
                        <span style={{ color: style.color, fontSize: '10px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <ExternalLink size={10} /> open
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    {isLong && (
                      <button
                        onClick={(e) => toggleExpand(e, index)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6f6b7d', padding: '0', lineHeight: 1 }}
                      >
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                    )}
                    {isHovered && (
                      <Trash2
                        size={12} color="#EF4444"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); handleDelete(notifId); }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '30px 10px' }}>
          <Bell size={24} color="#6f6b7d" style={{ opacity: 0.5, marginBottom: '8px' }} />
          <p style={{ color: '#6f6b7d', fontSize: '12px', margin: 0 }}>All caught up!</p>
        </div>
      )}

      {pickerNotif && (
        <VideoPickerModal
          notif={pickerNotif}
          onSelect={(video) => {
            setPickerNotif(null);
            setEmbedVideo({ videoId: video.videoId, title: video.title });
          }}
          onClose={() => setPickerNotif(null)}
        />
      )}

      {embedVideo && (
        <VideoEmbedModal
          videoId={embedVideo.videoId}
          title={embedVideo.title}
          onClose={() => setEmbedVideo(null)}
        />
      )}
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return 'just now';
  const diffMinutes = Math.floor((Date.now() - new Date(dateString)) / 60000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

export default NotificationsPanel;
